import { useState, useEffect, useCallback, useRef } from 'react';
import PageHeader from '../../components/common/PageHeader';
import StatusBadge from '../../components/common/StatusBadge';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { usePaginacao } from '../../hooks/usePaginacao';
import { useSortable } from '../../hooks/useSortable';
import * as api from '../../api/empresas';

function slugify(v) {
  return v.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function Modal({ item, onClose, onSaved }) {
  const editando = !!item?.id_empresa_operadora;
  const [form, setForm] = useState({
    nome: item?.nome || '',
    slug: item?.slug || '',
    prefixo_pedido: item?.prefixo_pedido || '',
    cor_primaria: item?.cor_primaria || '#070340',
    cor_primaria_hover: item?.cor_primaria_hover || '#201D54',
    cor_accent: item?.cor_accent || '#D40018',
  });
  const [slugManual, setSlugManual] = useState(editando);
  const [logoFile, setLogoFile] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const fileLogoRef = useRef(null);
  const fileFaviconRef = useRef(null);

  const set = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const setNome = (v) => {
    setForm(f => ({ ...f, nome: v, slug: slugManual ? f.slug : slugify(v) }));
  };

  const salvar = async (e) => {
    e.preventDefault();
    setErro('');
    setLoading(true);
    try {
      let id = item?.id_empresa_operadora;
      if (editando) {
        await api.updateEmpresa(id, {
          nome: form.nome, cor_primaria: form.cor_primaria,
          cor_primaria_hover: form.cor_primaria_hover, cor_accent: form.cor_accent,
        });
      } else {
        const { data } = await api.createEmpresa(form);
        id = data.id_empresa_operadora;
      }
      if (logoFile) await api.uploadLogo(id, logoFile);
      if (faviconFile) await api.uploadFavicon(id, faviconFile);
      onSaved();
    } catch (err) {
      const d = err.response?.data?.detail;
      setErro(typeof d === 'string' ? d : 'Erro ao salvar.');
    } finally { setLoading(false); }
  };

  const toggleAtivo = async () => {
    setLoading(true);
    try {
      await api.updateEmpresa(item.id_empresa_operadora, { ativo: !item.ativo });
      onSaved();
    } finally { setLoading(false); }
  };

  return (
    <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.45)' }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title mb-0">{editando ? 'Editar Empresa' : 'Nova Empresa Operadora'}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <form onSubmit={salvar}>
            <div className="modal-body">
              {erro && <div className="alert alert-danger py-2">{erro}</div>}

              <div className="mb-3">
                <label className="form-label fw-semibold">Nome *</label>
                <input className="form-control" value={form.nome} onChange={e => setNome(e.target.value)} required autoFocus />
              </div>

              {!editando && (
                <>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Slug *</label>
                    <input name="slug" className="form-control" value={form.slug}
                      onChange={e => { setSlugManual(true); set(e); }}
                      placeholder="ex: cjc" required />
                    <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                      Usado na URL de login (ex: /e/{form.slug || '...'}/login) — não pode ser alterado depois.
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Prefixo do Pedido *</label>
                    <input name="prefixo_pedido" className="form-control" style={{ maxWidth: 160 }}
                      value={form.prefixo_pedido} onChange={set} placeholder="ex: CJC" required />
                    <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                      Prefixo da numeração de pedidos (ex: CJC-2026-0001) — não pode ser alterado depois.
                    </div>
                  </div>
                </>
              )}

              <label className="form-label fw-semibold d-block">Cores da marca</label>
              <div className="row g-2 mb-3">
                <div className="col-4">
                  <label className="form-label" style={{ fontSize: 12 }}>Primária</label>
                  <input name="cor_primaria" type="color" className="form-control form-control-color w-100"
                    value={form.cor_primaria} onChange={set} />
                </div>
                <div className="col-4">
                  <label className="form-label" style={{ fontSize: 12 }}>Primária (hover)</label>
                  <input name="cor_primaria_hover" type="color" className="form-control form-control-color w-100"
                    value={form.cor_primaria_hover} onChange={set} />
                </div>
                <div className="col-4">
                  <label className="form-label" style={{ fontSize: 12 }}>Destaque</label>
                  <input name="cor_accent" type="color" className="form-control form-control-color w-100"
                    value={form.cor_accent} onChange={set} />
                </div>
              </div>

              <div className="row g-2">
                <div className="col-6">
                  <label className="form-label fw-semibold">Logo</label>
                  <input ref={fileLogoRef} type="file" accept="image/*" className="form-control form-control-sm"
                    onChange={e => setLogoFile(e.target.files?.[0] || null)} />
                  {item?.logo_url && !logoFile && (
                    <img src={item.logo_url} alt="Logo atual" style={{ height: 32, marginTop: 6 }} />
                  )}
                </div>
                <div className="col-6">
                  <label className="form-label fw-semibold">Favicon</label>
                  <input ref={fileFaviconRef} type="file" accept="image/*" className="form-control form-control-sm"
                    onChange={e => setFaviconFile(e.target.files?.[0] || null)} />
                  {item?.favicon_url && !faviconFile && (
                    <img src={item.favicon_url} alt="Favicon atual" style={{ height: 24, marginTop: 6 }} />
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer d-flex justify-content-between">
              {editando ? (
                <button type="button" className={`btn btn-sm ${item.ativo ? 'btn-outline-danger' : 'btn-outline-success'}`}
                  onClick={toggleAtivo} disabled={loading}>
                  {item.ativo ? 'Desativar' : 'Ativar'}
                </button>
              ) : <span />}
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancelar</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
                  {loading ? <span className="spinner-border spinner-border-sm" /> : 'Salvar'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function Empresas() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [modal, setModal] = useState(null);
  const [erroCarregar, setErroCarregar] = useState('');

  const carregar = useCallback(() => {
    setLoading(true);
    setErroCarregar('');
    api.listEmpresas()
      .then(r => setItems(r.data))
      .catch(() => setErroCarregar('Erro ao carregar empresas. Tente novamente.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const filtrados = items.filter(i =>
    i.nome.toLowerCase().includes(busca.toLowerCase()) || i.slug.toLowerCase().includes(busca.toLowerCase())
  );
  const { sorted, Th } = useSortable(filtrados, 'nome');
  const { paginados, Paginacao } = usePaginacao(sorted);

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader titulo="Empresas Operadoras" subtitulo={`${items.length} cadastradas`}>
        <button className="btn btn-primary btn-sm" onClick={() => setModal({})}>+ Nova Empresa</button>
      </PageHeader>

      {erroCarregar && <div className="alert alert-danger py-2">{erroCarregar}</div>}

      <div className="mb-3">
        <input className="form-control" style={{ maxWidth: 360 }}
          placeholder="Buscar por nome ou slug..." value={busca} onChange={e => setBusca(e.target.value)} />
      </div>

      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  <th style={{ width: 48 }}></th>
                  <Th campo="nome">Nome</Th>
                  <Th campo="slug">Slug</Th>
                  <Th campo="prefixo_pedido">Prefixo</Th>
                  <Th campo="ativo">Status</Th>
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {paginados.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-muted py-4">Nenhuma empresa cadastrada.</td></tr>
                )}
                {paginados.map(e => (
                  <tr key={e.id_empresa_operadora}>
                    <td>
                      {e.logo_url ? (
                        <img src={e.logo_url} alt={e.nome} style={{ height: 24 }} />
                      ) : (
                        <span style={{ display: 'inline-block', width: 20, height: 20, borderRadius: 4, background: e.cor_primaria }} />
                      )}
                    </td>
                    <td className="fw-semibold">{e.nome}</td>
                    <td><code>{e.slug}</code></td>
                    <td>{e.prefixo_pedido}</td>
                    <td><StatusBadge status={e.ativo ? 'ativo' : 'inativo'} /></td>
                    <td>
                      <button className="btn btn-sm btn-outline-primary py-0 px-2"
                        onClick={() => setModal(e)}>Editar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <Paginacao />

      {modal !== null && (
        <Modal item={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); carregar(); }} />
      )}
    </div>
  );
}
